/*
 * @copyright Copyright (c) valantic CEC Deutschland GmbH (https://www.valantic.com)
 * @see PROJECT_LICENSE.txt
 */

let sharedCardVisibilityObserver = null

/**
 * Escape a string for use in a CSS selector.
 *
 * @param value
 * @returns {*|string}
 */
const escapeSelector = (value) => {
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
        return CSS.escape(value)
    }

    return value.replace(/([:\\[\\]\\.#])/g, '\\$1')
}

const ensureVisibilityState = (context) => {
    if (!context.activeProductOrder) {
        context.activeProductOrder = []
    }
    if (!context.activeProductHandlers) {
        context.activeProductHandlers = {}
    }
    if (!context.visibleProductSet) {
        context.visibleProductSet = new Set()
    }
    if (!context.sliderResetTimers) {
        context.sliderResetTimers = {}
    }
}

const getCardVisibilityObserver = () => {
    if (typeof IntersectionObserver === 'undefined') {
        return null
    }

    if (!sharedCardVisibilityObserver) {
        sharedCardVisibilityObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                const target = entry.target
                const data = target.__liveSearchCardVisibility
                if (!data) {
                    return
                }

                if (entry.isIntersecting) {
                    data.setVisible(true)
                } else {
                    data.setVisible(false)
                }
            })
        }, {
            root: null,
            rootMargin: '100px 0px 0px 0px',
            threshold: 0
        })
    }

    return sharedCardVisibilityObserver
}

const liveSearchProductsVisibility = {
    activeProductOrder: [],
    activeProductHandlers: {},
    visibleProductSet: null,
    /**
     * Register an element for shared visibility observation.
     *
     * @param {HTMLElement} element
     * @param {Function} setVisible
     */
    registerCardVisibility(element, setVisible) {
        const observer = getCardVisibilityObserver()
        if (!observer || !element) {
            setVisible(true)
            return
        }

        element.__liveSearchCardVisibility = { setVisible }
        observer.observe(element)
    },
    /**
     * Unregister a previously observed element.
     *
     * @param {HTMLElement} element
     */
    unregisterCardVisibility(element) {
        if (!sharedCardVisibilityObserver || !element) {
            return
        }
        sharedCardVisibilityObserver.unobserve(element)
        delete element.__liveSearchCardVisibility
    },
    /**
     * Track product visibility and enforce a maximum number of active sliders.
     *
     * @param {string} productKey
     * @param {boolean} isVisible
     * @param {Function} setVisible
     */
    handleCardVisibility(productKey, isVisible, setVisible) {
        if (!productKey || typeof setVisible !== 'function') {
            return
        }

        ensureVisibilityState(this)
        this.activeProductHandlers[productKey] = setVisible

        if (isVisible) {
            this.visibleProductSet.add(productKey)
            setVisible(true)
            if (typeof this.$nextTick === 'function') {
                this.$nextTick(() => this.queueSliderReset(productKey))
            } else {
                this.queueSliderReset(productKey)
            }
            this.touchActiveProduct(productKey)
            this.enforceActiveLimit()
            if (typeof this.tryStartSliderAnimation === 'function') {
                if (typeof this.$nextTick === 'function') {
                    this.$nextTick(() => this.tryStartSliderAnimation(productKey))
                } else {
                    this.tryStartSliderAnimation(productKey)
                }
            }
        } else {
            this.visibleProductSet.delete(productKey)
            this.clearSliderResetTimers(productKey)
        }
    },
    /**
     * Remove a product handler when the element is destroyed.
     *
     * @param {string} productKey
     */
    unregisterProductHandler(productKey) {
        if (!productKey) {
            return
        }

        ensureVisibilityState(this)
        this.clearSliderResetTimers(productKey)
        delete this.activeProductHandlers[productKey]
        this.visibleProductSet.delete(productKey)
        this.activeProductOrder = this.activeProductOrder.filter((entry) => entry !== productKey)
    },
    /**
     * Reset the active product cache after a product list reset.
     */
    resetActiveProductCache() {
        this.activeProductOrder = []
        this.activeProductHandlers = {}
        this.visibleProductSet = new Set()
        this.sliderResetTimers = {}
    },
    /**
     * Mark a product as recently active.
     *
     * @param sku
     */
    touchActiveProduct(sku) {
        ensureVisibilityState(this)
        this.activeProductOrder = this.activeProductOrder.filter((entry) => entry !== sku)
        this.activeProductOrder.push(sku)
    },
    enforceActiveLimit() {
        ensureVisibilityState(this)
        const pageSize = Number(this.pagesSize) || 0
        const limitMultiplier = Number(this.sliderActiveLimit) || 0
        if (limitMultiplier <= 0) {
            return
        }

        const limit = pageSize > 0 ? pageSize * limitMultiplier : 0
        if (!limit) {
            return
        }

        let guard = 0
        while (this.activeProductOrder.length > limit && guard < this.activeProductOrder.length) {
            const candidate = this.activeProductOrder[0]
            if (this.visibleProductSet.has(candidate)) {
                this.activeProductOrder.push(this.activeProductOrder.shift())
                guard += 1
                continue
            }

            this.activeProductOrder.shift()
            const handler = this.activeProductHandlers[candidate]
            if (handler) {
                handler(false)
            }
        }
    },
    /**
     * Reset slider scroll position after it becomes visible.
     *
     * @param {string} productKey
     */
    resetSliderScrollForProduct(productKey) {
        if (!productKey || typeof document === 'undefined') {
            return
        }

        if (typeof this.isTouchDevice === 'function' && !this.isTouchDevice()) {
            return
        }

        const productList = document.getElementById('product-list')
        const selector = `[data-product-instance-key="${escapeSelector(String(productKey))}"]`
        const product = productList?.querySelector(selector) || document.querySelector(selector)
        if (!product) {
            return
        }

        const track = product.querySelector('[x-ref="sliderTrack"]')
        if (!track) {
            return
        }

        const reset = () => {
            if (typeof track.scrollTo === 'function') {
                track.scrollTo({ left: 0 })
            }

            track.scrollLeft = 0
        }

        reset()
        if (typeof requestAnimationFrame === 'function') {
            requestAnimationFrame(() => {
                reset()
                requestAnimationFrame(reset)
            })
        }
    },
    /**
     * Queue multiple reset attempts to avoid iOS momentum scroll offsets.
     *
     * @param {string} productKey
     */
    queueSliderReset(productKey) {
        if (!productKey) {
            return
        }

        if (typeof this.isTouchDevice === 'function' && !this.isTouchDevice()) {
            return
        }

        ensureVisibilityState(this)
        this.clearSliderResetTimers(productKey)

        const delays = [0, 120, 260]
        this.sliderResetTimers[productKey] = delays.map((delay) => setTimeout(() => {
            this.resetSliderScrollForProduct(productKey)
        }, delay))
    },
    /**
     * Clear pending reset attempts for a slider.
     *
     * @param {string} productKey
     */
    clearSliderResetTimers(productKey) {
        if (!productKey || !this.sliderResetTimers?.[productKey]) {
            return
        }

        this.sliderResetTimers[productKey].forEach((timerId) => clearTimeout(timerId))
        delete this.sliderResetTimers[productKey]
    }
}

export default liveSearchProductsVisibility
